import React, { useEffect, useMemo, useState } from "react";
import {
  assignReportToVehicle,
  flagReportAccountIssue,
  mergeVehicles,
  requestMissingSample,
  updateVehicleDetails,
} from "../../services/api";

function clean(value) {
  return String(value || "").trim();
}

function normalize(value) {
  return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function normalizeVin(value) {
  return clean(value).toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function isValidVin(value) {
  const vin = normalizeVin(value);
  return vin.length === 17 && !/[IOQ]/.test(vin);
}

function sampleDate(sample) {
  return sample?.sample_tested_date || sample?.response_submission_timestamp || "";
}

function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
}

function snapshotFromGroup(group) {
  const sample = group?.latestSample || {};
  return {
    id: group?.id,
    label: group?.label || "Unknown Vehicle",
    vehicle_year: clean(sample.vehicle_year) || null,
    vehicle_make: clean(sample.vehicle_make) || null,
    vehicle_model: clean(sample.vehicle_model) || null,
    vehicle_vin: clean(sample.vehicle_vin) || null,
  };
}

function compatibleGroups(a, b) {
  const aSample = a?.latestSample || {};
  const bSample = b?.latestSample || {};
  const makeA = normalize(aSample.vehicle_make);
  const makeB = normalize(bSample.vehicle_make);
  const modelA = normalize(aSample.vehicle_model);
  const modelB = normalize(bSample.vehicle_model);
  if (!makeA || !makeB || !modelA || !modelB) return false;
  if (makeA !== makeB || modelA !== modelB) return false;
  const yearA = normalize(aSample.vehicle_year);
  const yearB = normalize(bSample.vehicle_year);
  return !yearA || !yearB || yearA === yearB;
}

const s = {
  wrap: {
    border: "1px solid var(--border)", borderRadius: "var(--dashboard-card-radius)",
    background: "var(--bg-card)", padding: 16, marginBottom: 24,
    boxShadow: "var(--dashboard-card-shadow)",
  },
  title: { fontSize: 14, fontWeight: 800, color: "var(--text-primary)", marginBottom: 10 },
  row: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 10 },
  input: {
    minHeight: 38, borderRadius: "var(--dashboard-control-radius)", border: "1.5px solid var(--border)",
    background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", padding: "8px 10px",
    fontSize: 13,
  },
  select: {
    minHeight: 38, minWidth: 230, borderRadius: "var(--dashboard-control-radius)",
    border: "1.5px solid var(--border)", background: "var(--dashboard-selector-bg)",
    color: "var(--text-primary)", padding: "8px 10px", fontSize: 13,
  },
  button: {
    minHeight: 38, border: "none", borderRadius: "var(--dashboard-control-radius)",
    background: "var(--teal)", color: "#fff", padding: "8px 13px",
    fontSize: 13, fontWeight: 800, cursor: "pointer",
  },
  secondaryButton: {
    minHeight: 38, border: "1.5px solid var(--border)", borderRadius: "var(--dashboard-control-radius)",
    background: "var(--dashboard-selector-bg)", color: "var(--text-primary)",
    padding: "8px 13px", fontSize: 13, fontWeight: 800, cursor: "pointer",
  },
  dangerButton: {
    minHeight: 38, border: "1.5px solid #fecaca", borderRadius: "var(--dashboard-control-radius)",
    background: "#fef2f2", color: "#b91c1c", padding: "8px 13px",
    fontSize: 13, fontWeight: 800, cursor: "pointer",
  },
  check: { display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--text-muted)" },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 6, minWidth: 230, flex: "1 1 260px" },
  note: { fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5, marginTop: 6 },
  warningNote: { fontSize: 12, color: "#dc2626", lineHeight: 1.45, marginTop: 4, maxWidth: 560 },
  status: { fontSize: 13, fontWeight: 700, marginTop: 10 },
  ok: { color: "#16a34a" },
  error: { color: "#dc2626" },
  panel: { borderTop: "1px solid var(--border-subtle)", marginTop: 12, paddingTop: 12 },
};

export default function VehicleCorrectionActions({
  samples,
  vehicleGroups,
  selectedGroup,
  selectedSample,
  customerEmail = "",
  onChanged,
  embedded = false,
}) {
  const [mode, setMode] = useState(null);
  const [visibleSampleId, setVisibleSampleId] = useState("");
  const [missingSampleId, setMissingSampleId] = useState("");
  const [mergeSourceId, setMergeSourceId] = useState("");
  const [vehicleNameInput, setVehicleNameInput] = useState("");
  const [vinInput, setVinInput] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const otherReports = useMemo(() => {
    if (!selectedGroup) return [];
    return samples.filter((sample) => !selectedGroup.samples.some((current) => current.lab_sample_id === sample.lab_sample_id));
  }, [samples, selectedGroup]);

  const mergeCandidates = useMemo(() => {
    if (!selectedGroup) return [];
    return vehicleGroups.filter((group) => group.id !== selectedGroup.id && compatibleGroups(group, selectedGroup));
  }, [vehicleGroups, selectedGroup]);

  const hasOtherVehicles = useMemo(() => {
    if (!selectedGroup) return false;
    return vehicleGroups.some((group) => group.id !== selectedGroup.id);
  }, [vehicleGroups, selectedGroup]);

  useEffect(() => {
    const sample = selectedGroup?.latestSample || {};
    setVehicleNameInput(selectedGroup?.label || "");
    setVinInput(clean(sample.vehicle_vin));
  }, [selectedGroup?.id, selectedGroup?.label, selectedGroup?.latestSample?.vehicle_vin]);

  function open(nextMode) {
    setMode(mode === nextMode ? null : nextMode);
    setConfirmed(false);
    setStatus(null);
  }

  function cancel() {
    setMode(null);
    setVisibleSampleId("");
    setMissingSampleId("");
    setMergeSourceId("");
    setConfirmed(false);
    setStatus(null);
  }

  async function submit(action) {
    setBusy(true);
    setStatus(null);
    try {
      await action();
      setStatus({ type: "ok", text: "Correction submitted." });
      setConfirmed(false);
      if (onChanged) await onChanged();
    } catch (err) {
      setStatus({ type: "error", text: err.response?.data?.error || "Correction could not be submitted." });
    } finally {
      setBusy(false);
    }
  }

  if (!selectedGroup) return null;
  const moveVisibleReportDisabled = busy || !visibleSampleId || !confirmed;
  const currentVin = clean(selectedGroup.latestSample?.vehicle_vin);
  const currentVehicleName = selectedGroup.label || "";
  const vehicleNameSaveDisabled = busy || !clean(vehicleNameInput) || clean(vehicleNameInput) === currentVehicleName;
  const vinSaveDisabled = busy || !clean(vinInput) || normalizeVin(vinInput) === normalizeVin(currentVin);
  const showVinWarning = !isValidVin(vinInput);
  const vehicleSnapshot = snapshotFromGroup(selectedGroup);
  const showMoveExistingReport = hasOtherVehicles && otherReports.length > 0;

  return (
    <div style={embedded ? { padding: 0 } : s.wrap}>
      <div style={s.title}>Vehicle corrections</div>
      <div style={s.note}>
        Use these when a report belongs under a different vehicle, two vehicle tabs are the same vehicle, or a report does not belong to your account.
      </div>

      <div style={s.panel}>
        <div style={s.title}>Change Vehicle Name</div>
        <div style={s.row}>
          <label style={s.fieldGroup}>
            <input
              style={s.input}
              value={vehicleNameInput}
              onChange={e => setVehicleNameInput(e.target.value)}
              placeholder="Preferred vehicle name"
            />
          </label>
          <button
            type="button"
            style={{ ...s.button, opacity: vehicleNameSaveDisabled ? 0.5 : 1 }}
            disabled={vehicleNameSaveDisabled}
            onClick={() => submit(() => updateVehicleDetails({
              customerEmail,
              vehicleGroupId: selectedGroup.id,
              vehicleSnapshot,
              label: vehicleNameInput,
              vin: vinInput,
            }))}
          >
            Save Vehicle Name
          </button>
        </div>
      </div>

      <div style={s.panel}>
        <div style={s.title}>Update VIN</div>
        <div style={s.row}>
          <label style={s.fieldGroup}>
            <input
              style={s.input}
              value={vinInput}
              onChange={e => setVinInput(e.target.value.toUpperCase())}
              placeholder="Enter VIN"
            />
            {showVinWarning && (
              <span style={s.warningNote}>
                This doesn't seem to be a valid VIN. Please update as soon as possible. It helps us manage all this data correctly.
              </span>
            )}
          </label>
          <button
            type="button"
            style={{ ...s.button, opacity: vinSaveDisabled ? 0.5 : 1 }}
            disabled={vinSaveDisabled}
            onClick={() => submit(() => updateVehicleDetails({
              customerEmail,
              vehicleGroupId: selectedGroup.id,
              vehicleSnapshot,
              label: vehicleNameInput,
              vin: vinInput,
            }))}
          >
            Save VIN
          </button>
        </div>
      </div>

      <div style={s.row}>
        <button type="button" style={s.secondaryButton} onClick={() => open("add")}>
          {hasOtherVehicles ? "Move report to this vehicle" : "Add a Sample ID to this vehicle"}
        </button>
        {mergeCandidates.length > 0 && (
          <button type="button" style={s.secondaryButton} onClick={() => open("merge")}>Merge vehicles</button>
        )}
        {selectedSample && (
          <button type="button" style={s.dangerButton} onClick={() => open("flag")}>Report does not belong to me</button>
        )}
      </div>

      {mode === "add" && (
        <div style={s.panel}>
          {showMoveExistingReport && (
            <>
              <div style={s.title}>Move an existing report to this vehicle</div>
              <div style={s.row}>
                <select style={s.select} value={visibleSampleId} onChange={e => setVisibleSampleId(e.target.value)}>
                  <option value="">Choose a visible report</option>
                  {otherReports.map(sample => (
                    <option key={sample.lab_sample_id} value={sample.lab_sample_id}>
                      {sample.lab_sample_id} - {sample.component_type || "Oil"} - {formatDate(sampleDate(sample))}
                    </option>
                  ))}
                </select>
                <label style={s.check}>
                  <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                  I confirm this report belongs to {selectedGroup.label}.
                </label>
                <button
                  type="button"
                  style={{ ...s.button, opacity: moveVisibleReportDisabled ? 0.5 : 1 }}
                  disabled={moveVisibleReportDisabled}
                  onClick={() => submit(() => assignReportToVehicle({
                    customerEmail,
                    labSampleId: visibleSampleId,
                    targetVehicleGroupId: selectedGroup.id,
                    targetVehicleSnapshot: snapshotFromGroup(selectedGroup),
                    confirmationAccepted: confirmed,
                  }))}
                >
                  Move visible report
                </button>
                <button type="button" style={s.secondaryButton} disabled={busy} onClick={cancel}>
                  Cancel
                </button>
              </div>
            </>
          )}
          <div style={{ ...s.title, marginTop: showMoveExistingReport ? 16 : 0 }}>Add a Sample ID to this vehicle</div>
          <div style={s.row}>
            <input
              style={s.input}
              value={missingSampleId}
              onChange={e => setMissingSampleId(e.target.value)}
              placeholder="Enter missing sample ID"
            />
            <button
              type="button"
              style={s.button}
              disabled={busy || !missingSampleId}
              onClick={() => submit(() => requestMissingSample({
                customerEmail,
                labSampleId: missingSampleId,
                targetVehicleGroupId: selectedGroup.id,
              }))}
            >
              Request review
            </button>
            <button type="button" style={s.secondaryButton} disabled={busy} onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {mode === "merge" && (
        <div style={s.panel}>
          <div style={s.row}>
            <select style={s.select} value={mergeSourceId} onChange={e => setMergeSourceId(e.target.value)}>
              <option value="">Choose vehicle to merge into {selectedGroup.label}</option>
              {mergeCandidates.map(group => (
                <option key={group.id} value={group.id}>{group.label} - {group.subtitle}</option>
              ))}
            </select>
            <label style={s.check}>
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
              I confirm these are the same vehicle.
            </label>
            <button
              type="button"
              style={s.button}
              disabled={busy || !mergeSourceId || !confirmed}
              onClick={() => submit(() => mergeVehicles({
                customerEmail,
                sourceVehicleGroupId: mergeSourceId,
                targetVehicleGroupId: selectedGroup.id,
                confirmationAccepted: confirmed,
              }))}
            >
              Merge into selected vehicle
            </button>
            <button type="button" style={s.secondaryButton} disabled={busy} onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {mode === "flag" && selectedSample && (
        <div style={s.panel}>
          <div style={s.note}>
            This keeps report {selectedSample.lab_sample_id} visible while SPEEDiagnostix reviews the account association.
          </div>
          <div style={s.row}>
            <label style={s.check}>
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
              I confirm this report does not belong to my account.
            </label>
            <button
              type="button"
              style={s.dangerButton}
              disabled={busy || !confirmed}
              onClick={() => submit(() => flagReportAccountIssue({
                customerEmail,
                labSampleId: selectedSample.lab_sample_id,
                confirmationAccepted: confirmed,
              }))}
            >
              Flag for review
            </button>
            <button type="button" style={s.secondaryButton} disabled={busy} onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {status && <div style={{ ...s.status, ...(status.type === "ok" ? s.ok : s.error) }}>{status.text}</div>}
    </div>
  );
}
